import type { ReactNode } from "react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { marketingFontVariables } from "@/lib/marketing-fonts";

type LandingPageShellProps = {
  /** The page's sections, rendered between the navbar and the footer. */
  children: ReactNode;
  /**
   * Page background. A prop rather than a `className` override because two background
   * utilities on one element is a coin-flip decided by CSS source order, not by the order
   * they appear in the string.
   */
  surface?: "default" | "white";
  /** Extra classes on <main>, for page-scoped hooks such as b2b's `b2b-page`. */
  className?: string;
};

const surfaceClassNames = {
  default: "bg-mkt-surface",
  white: "bg-white",
} as const;

/**
 * The frame every landing-page route renders inside: marketing fonts, the fixed navbar,
 * the page's sections, and the footer.
 *
 * This exists because five call sites had drifted into five slightly different copies of the
 * same wrapper — one had `relative`, one swapped the background, one wrapped its content in
 * an extra `flex-1` div, and every one of them had to independently remember `pt-20`. That
 * last one is the important part: `Navbar` is `position: fixed`, so it occupies no space in
 * flow, and a page that forgets the offset renders its first section underneath the nav. It
 * was previously enforced by a comment in Navbar.tsx. Now it is impossible to get wrong,
 * because no page writes the wrapper at all.
 *
 * `marketingFontVariables` stays on <main> rather than moving up to <body>: the dashboard
 * and admin areas share that <body> and must keep their own Geist/Montserrat typography.
 */
export default function LandingPageShell({
  children,
  surface = "default",
  className = "",
}: LandingPageShellProps) {
  return (
    <main
      className={`${marketingFontVariables} relative flex min-h-screen flex-col ${surfaceClassNames[surface]} pt-20 ${className}`}
    >
      <Navbar />
      {/* Grows to fill a short page so the footer sits at the bottom of the viewport rather
          than floating mid-screen with blank space beneath it. */}
      <div className="flex-1">{children}</div>
      <Footer />
    </main>
  );
}
